import { DefaultGameState, GameState } from "@shared/types";
import styles from "../../styles";
import { GameRouteService } from "../../services/GameRouteService";

interface MinigameResponse {
    count: number;
    isComplete: boolean;
}

interface PopupElement extends HTMLElement {
    content: string;
}

export class MiniGameComponent extends HTMLElement {
    private count: number = 0;
    private _onClose: (() => void) | null = null;
    private gameRouteService: GameRouteService = new GameRouteService();

    public constructor() {
        super();
    }

    public set onClose(handler: () => void) {
        this._onClose = handler;
    }

    public async connectedCallback(): Promise<void> {
        const state: GameState = await this.gameRouteService.getGameState();
        const currentGameState: DefaultGameState = state as DefaultGameState;
        if (currentGameState.roomAlias === "gym") {
            this.render();
            this.setupEventListeners();
            void this.fetchCurrentCount();
        }
    }

    public disconnectedCallback(): void {
        this.removeEventListeners();
    }

    private render(): void {
        const content: string = `
        <style>
            ${styles.popups}
            .game-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 20px;
                background-color: #222;
                border-radius: 8px;
                min-width: 300px;
            }
            .counter {
                font-size: 24px;
                margin: 20px 0;
                color: #ddd;
                text-align: center;
            }
            .progress-bar {
                width: 100%;
                height: 20px;
                background-color: #333;
                border: 1px solid #555;
                border-radius: 5px;
                overflow: hidden;
                margin: 10px 0;
                box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.2);
            }
            .progress-fill {
                height: 100%;
                background-color: #3498db;
                transition: width 0.3s ease;
            }
            .button-container {
                display: flex;
                flex-wrap: wrap;
                gap: 10px;
                justify-content: center;
                margin: 20px 0;
            }
            .game-button {
                background-color: #333;
                color: #ddd;
                border: 1px solid #555;
                border-radius: 5px;
                padding: 8px 15px;
                cursor: pointer;
                transition: all 0.2s ease;
                font-size: 18px;
            }
            .game-button:hover:not(:disabled) {
                background-color: #444;
                transform: translateY(-2px);
                box-shadow: 0 5px 10px rgba(0,0,0,0.3);
            }
            #moveUpButton {
                background-color: #3498db;
                color: #fff;
                border: 1px solid #3498db;
            }
            #moveUpButton:hover:not(:disabled) {
                background-color: #2980b9;
            }
            #moveDownButton {
                background-color: #e74c3c;
                color: #fff;
                border: 1px solid #e74c3c;
            }
            #moveDownButton:hover:not(:disabled) {
                background-color: #c0392b;
            }
            #moveDownButton:disabled {
                background-color: #2a2a2a;
                color: #666;
                border-color: #444;
                cursor: not-allowed;
                transform: none;
                box-shadow: none;
            }
            #resetButton {
                background-color: #ff6b6b;
                color: #fff;
                border: 1px solid #ff6b6b;
            }
            #resetButton:hover {
                background-color: #ff4757;
            }
            #viewDetailsButton {
                background-color: #333;
                color: #ddd;
                border: 1px solid #555;
            }
            #viewDetailsButton:hover {
                background-color: #444;
            }
            .game-status {
                margin-top: 15px;
                font-style: italic;
                color: #bdc3c7;
                text-align: center;
            }
            h2 {
                color: #ddd;
                text-align: center;
                margin-bottom: 15px;
            }
            .timer {
                margin-top: 20px;
                font-size: 18px;
                color: #ddd;
                text-align: center;
            }
        </style>
        <div class="popup-overlay">
            <div class="popup" style="width: 100%; max-width: 500px;">
                <div class="popup-close">&times;</div>
                <div class="popup-content">
                    <div class="game-container">
                        <h2>Training Minigame</h2>
                            <!-- Dynamische afbeelding -->

                           <img src="../assets/img/action/moveup.png" id="moveUpImage" style="display: none;" height="320px">
                           <img src="../assets/img/action/movedown.png" id="moveDownImage" style="display: block;" height="320px">

                            
                        <div class="counter">Level: <span id="count">0</span>/15</div>
                        <div class="progress-bar">
                            <div class="progress-fill" style="width: ${(this.count / 15) * 100}%"></div>
                        </div>
                        <div class="button-container">
                            <button id="moveUpButton" class="game-button">Move up</button>
                            <button id="moveDownButton" class="game-button" disabled>Move down</button>
                        </div>
                        <div class="timer" id="timerDisplay">Time left: 10s</div> <!-- Hier komt de timer -->
                        <div class="game-status" id="gameStatus">Ready to start training!</div>
                        <div class="button-container">
                            <button id="resetButton" class="game-button">Reset Progress</button>
                            <button id="viewDetailsButton" class="game-button">View Details</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;
        this.innerHTML = content;
    }

    private setupEventListeners(): void {
        const moveUpButton: HTMLButtonElement | null = this.querySelector<HTMLButtonElement>("#moveUpButton");
        const moveDownButton: HTMLButtonElement | null = this.querySelector<HTMLButtonElement>("#moveDownButton");
        const resetButton: HTMLButtonElement | null = this.querySelector<HTMLButtonElement>("#resetButton");
        const viewDetailsButton: HTMLButtonElement | null = this.querySelector<HTMLButtonElement>("#viewDetailsButton");
        const closeButton: HTMLElement | null = this.querySelector(".popup-close");
        const overlay: HTMLElement | null = this.querySelector(".popup-overlay");
        const popup: HTMLElement | null = this.querySelector(".popup");

        // Hier vervangen we de oude event listener voor de 'Move Up' knop door de nieuwe
        if (moveUpButton) moveUpButton.addEventListener("click", () => this.handleMoveUp()); // Gebruik handleMoveUp

        if (moveDownButton) moveDownButton.addEventListener("click", () => this.handleMoveDown());
        if (resetButton) resetButton.addEventListener("click", () => this.resetCount());
        if (viewDetailsButton) viewDetailsButton.addEventListener("click", () => this.showMinigamePopup());
        if (closeButton) closeButton.addEventListener("click", this.handleClose.bind(this));
        if (overlay) overlay.addEventListener("click", this.handleOutsideClick.bind(this));
        if (popup) popup.addEventListener("click", (e: Event) => e.stopPropagation());
    }

    private timer: number | null = null;
    private timeLeft: number = 10;

    private startTimer(): void {
    // Alleen starten als de timer nog niet is gestart én de minigame niet is voltooid
        if (this.timer !== null || this.count === 15) return; // Voorkom dat de timer start als de minigame is behaald

        this.timeLeft = 10;
        this.updateTimerDisplay();

        this.timer = window.setInterval(() => {
            this.timeLeft--;
            this.updateTimerDisplay();

            if (this.timeLeft <= 0) {
                void this.resetCount();
                this.stopTimer();
            }
        }, 1000);
    }

    private stopTimer(): void {
        if (this.timer !== null) {
            clearInterval(this.timer);
            this.timer = null;
        }
    }

    private updateTimerDisplay(): void {
        const timerElement: HTMLElement | null = this.querySelector("#timerDisplay");
        if (timerElement) {
            timerElement.textContent = `Time left: ${this.timeLeft}s`;
        }
    }

    private removeEventListeners(): void {
        const closeButton: HTMLElement | null = this.querySelector(".popup-close");
        const overlay: HTMLElement | null = this.querySelector(".popup-overlay");
        const popup: HTMLElement | null = this.querySelector(".popup");

        if (closeButton) closeButton.removeEventListener("click", this.handleClose.bind(this));
        if (overlay) overlay.removeEventListener("click", this.handleOutsideClick.bind(this));
        if (popup) popup.removeEventListener("click", (e: Event) => e.stopPropagation());
    }

    private handleClose(): void {
        if (this._onClose) {
            this._onClose();
        }
        this.remove();
    }

    private handleOutsideClick(event: MouseEvent): void {
        if (event.target === this.querySelector(".popup-overlay")) {
            this.handleClose();
        }
    }

    private enableMoveDown(): void {
    // Als de teller onder de 15 staat, maak de Move Down knop beschikbaar
        if (this.count < 15) {
            const moveDownButton: HTMLButtonElement | null = this.querySelector<HTMLButtonElement>("#moveDownButton");
            if (moveDownButton) {
                moveDownButton.removeAttribute("disabled");
                moveDownButton.textContent = "Move down";
            }

            const gameStatus: Element | null = this.querySelector("#gameStatus");
            if (gameStatus) {
                gameStatus.textContent = "Move down available! Click to continue training.";
            }

            // Start de timer als de teller 0 is en de Move Up knop wordt ingedrukt
            if (this.count === 0) {
                this.startTimer();
            }
        }
    }

    private async handleMoveUp(): Promise<void> {
        if (this.count === 0) {
            this.startTimer();
        }

        await this.updateCount();
        this.enableMoveDown();

        // Laat alleen de Move Up afbeelding zien
        this.querySelector<HTMLImageElement>("#moveUpImage")!.style.display = "block";
        this.querySelector<HTMLImageElement>("#moveDownImage")!.style.display = "none";

        this.querySelector<HTMLButtonElement>("#moveUpButton")?.setAttribute("disabled", "true");
    }

    private async handleMoveDown(): Promise<void> {
        await this.updateCount();

        this.querySelector<HTMLButtonElement>("#moveDownButton")?.setAttribute("disabled", "true");
        this.querySelector<HTMLButtonElement>("#moveUpButton")?.removeAttribute("disabled");

        // Laat alleen de Move Down afbeelding zien
        this.querySelector<HTMLImageElement>("#moveUpImage")!.style.display = "none";
        this.querySelector<HTMLImageElement>("#moveDownImage")!.style.display = "block";
    }

    private async updateCount(): Promise<void> {
        try {
            const data: MinigameResponse | undefined = await this.gameRouteService.updateCount();
            if (data !== undefined) {
                await this.updateCounter(data.count, data.isComplete);
            }
            else {
                console.error("Did not get expected values from http request");
            }
        }
        catch (error) {
            console.error(error);
        }
    }

    private async fetchCurrentCount(): Promise<void> {
        try {
            const data: MinigameResponse | undefined = await this.gameRouteService.fetchCurrentCount();
            if (data !== undefined) {
                await this.updateCounter(data.count, data.isComplete);

                // Als de game voltooid is, update de player session zodat de dumbbell gepakt kan worden
                if (data.isComplete) {
                    console.log("Minigame voltooid! Dumbbell kan nu worden opgepakt.");
                }
            }
            else {
                console.error("Did not get expected values from http request");
            }
        }
        catch (error) {
            console.error(error);
        }
    }

    private async resetCount(): Promise<void> {
        try {
            const data: MinigameResponse | undefined = await this.gameRouteService.resetCount();
            if (data !== undefined) {
                await this.updateCounter(data.count, data.isComplete);
                // Als de minigame is gereset, reset de game naar de beginwaarden en start de timer opnieuw
                this.resetGame();
            }
            else {
                console.error("Did not get expected values from http request");
            }
        }
        catch (error) {
            console.error(error);
        }
    }

    private async updateCounter(count: number, isComplete: boolean): Promise<void> {
        this.count = count;
        const countElement: Element | null = this.querySelector("#count");
        const progressFill: Element | null = this.querySelector(".progress-fill");
        const gameStatus: Element | null = this.querySelector("#gameStatus");
        const resetButton: HTMLButtonElement | null = this.querySelector<HTMLButtonElement>("#resetButton");
        const moveUpButton: HTMLButtonElement | null = this.querySelector<HTMLButtonElement>("#moveUpButton");
        const moveDownButton: HTMLButtonElement | null = this.querySelector<HTMLButtonElement>("#moveDownButton");

        if (countElement) countElement.textContent = `${this.count}`;
        if (progressFill) {
            progressFill.setAttribute("style", `width: ${(this.count / 15) * 100}%`);
        }

        if (gameStatus) {
            if (isComplete) {
                gameStatus.textContent = "Training Complete! You've reached maximum level!";

                moveUpButton?.setAttribute("disabled", "true");
                moveDownButton?.setAttribute("disabled", "true");

                this.stopTimer();

                if (resetButton) resetButton.style.display = "none";

                // **Hier de API-call naar finishCount**
                await this.finishCount();

                // Dispatch event wanneer de minigame voltooid is
                this.dispatchEvent(new CustomEvent("minigameComplete"));
            }
            else if (this.count === 0) {
                gameStatus.textContent = "Ready to start training!";
            }
            else {
                gameStatus.textContent = `Keep going! ${15 - this.count} more levels to go!`;
            }
        }

        if (isComplete) {
            setTimeout(() => {
                // alert("Congratulations! You've completed your training!");
            }, 500);
        }
    }

    private async finishCount(): Promise<void> {
        try {
            const response: MinigameResponse | undefined = await this.gameRouteService.finishCount();
            if (response) {
                console.log("Minigame voltooid! Dumbbell kan nu worden opgepakt.");
            }
            else {
                console.error("Finish count kon niet worden voltooid.");
            }
        }
        catch (error) {
            console.error("Fout bij finishCount:", error);
        }
    }

    private resetGame(): void {
    // Reset the counter to 0
        this.count = 0;

        // Reset progress bar and status message
        const countElement: Element | null = this.querySelector("#count");
        const progressFill: Element | null = this.querySelector(".progress-fill");
        const gameStatus: Element | null = this.querySelector("#gameStatus");
        const resetButton: HTMLButtonElement | null = this.querySelector<HTMLButtonElement>("#resetButton");
        const moveUpButton: HTMLButtonElement | null = this.querySelector<HTMLButtonElement>("#moveUpButton");

        if (countElement) countElement.textContent = `${this.count}`;
        if (progressFill) progressFill.setAttribute("style", "width: 0%");

        // Reset game status
        if (gameStatus) gameStatus.textContent = "Ready to start training!";

        // Show reset button and enable the Move Up button
        if (resetButton) resetButton.style.display = "inline-block";
        if (moveUpButton) moveUpButton.style.display = "inline-block"; // Ensure it's visible
        if (moveUpButton) moveUpButton.removeAttribute("disabled");

        // Stop the existing timer and reset the countdown
        this.stopTimer();
        this.timeLeft = 10;
        this.updateTimerDisplay();

        // Start the timer again
        this.startTimer();
    }

    /**
     * Toon een pop-up met de minigame details
     */
    private showMinigamePopup(): void {
        const popupElement: PopupElement = document.createElement("game-popup") as PopupElement;

        let popupContent: string;

        // Als de minigame voltooid is, toon een andere boodschap
        if (this.count === 15) {
            popupContent = `
            <div class="popup-container">
                <h2>Minigame Details</h2>
                <p>Current count is: <strong>${this.count}/15</strong></p>
                <p>You've trained hard, you're strong enough to pick up the dumbbell!</p>
            </div>
        `;
        }
        else {
            popupContent = `
            <div class="popup-container">
                <h2>Minigame Details</h2>
                <p>Current count is: <strong>${this.count}/15</strong></p>
                <p>Move up to increase the count and complete the minigame at 15.</p>
            </div>
        `;
        }

        popupElement.content = popupContent;
        document.body.appendChild(popupElement);
    }
}

if (!customElements.get("mini-game")) {
    customElements.define("mini-game", MiniGameComponent);
}
